import React, { useSyncExternalStore } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getModuleFlags, subscribeModuleFlags } from '../../access/moduleAccess';
import { useAuth } from '../auth/AuthProvider';
import { toLegacyPosPath, toPremiumPosPath } from './paths';

type Props = {
  /** Which POS module the wrapped page belongs to. */
  mode: 'legacy' | 'premium';
  children: React.ReactNode;
};

/**
 * Sends the user to the matching page of the POS module the company actually exposes
 * (`isPOSEnabled` -> premium, otherwise legacy), keeping query string, hash and state.
 * Waits for the company settings to load and follows later settings changes.
 */
export const PosModeRedirect: React.FC<Props> = ({ mode, children }) => {
  const location = useLocation();
  const { isAuthLoading } = useAuth();
  const { isPOSEnabled: premiumActive } = useSyncExternalStore(subscribeModuleFlags, getModuleFlags);

  if (isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" aria-busy="true">
        <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  const shouldRedirect = mode === 'premium' ? !premiumActive : premiumActive;
  if (shouldRedirect) {
    const pathname = mode === 'premium' ? toLegacyPosPath(location.pathname) : toPremiumPosPath(location.pathname);
    return <Navigate replace to={`${pathname}${location.search}${location.hash}`} state={location.state} />;
  }
  return <>{children}</>;
};
